import { useQuery } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'

/**
 * Cost of ownership of one asset (spec 2026-08-24-01, lot 2).
 *
 * Amounts are minor units (cents) of `currency`, except
 * `replacement_cost_estimate`, which is the equipment's own estimate in whole
 * currency units — the ratio is computed server-side so the two never drift.
 */
export interface CostOfOwnership {
  asset_uid: string
  window: { from: string; to: string; months: number }
  currency?: string
  total: number
  by_kind: Partial<Record<'preventive' | 'corrective' | 'works', number>>
  by_year: { year: number; amount: number }[]
  intervention_count: { total: number; preventive: number; corrective: number; works: number }
  /**
   * How each amount reached this asset. Displayed rather than hidden behind one
   * number: `shared` is a fair split, not a measurement, and the reader is
   * entitled to know which part of the total is which.
   */
  allocation: { direct: number; shared: number; declared: number }
  replacement_cost_estimate?: number | null
  ratio_replacement?: number | null
  /**
   * Spent on this asset's ancestors over the window with no link that could
   * reach it. NOT part of `total` — it measures how much of the picture is
   * missing, and is shown as such.
   */
  unallocated_on_ancestors: number
  last_intervention_at?: string | null
  /** The share carried by the asset's components (spec 2026-10-27-17), already in `total`. */
  components?: { count: number; amount: number } | null
}

async function fetchJSON<T>(url: string): Promise<T> {
  const token = localStorage.getItem('token')
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<T>
}

export function costOfOwnershipKey(org: string, assetUid: string, windowMonths?: number) {
  return ['cost-of-ownership', org, assetUid, windowMonths ?? 24] as const
}

/**
 * `enabled` is deliberately caller-controlled: the block only appears on
 * equipment, and a lot page must not fire this query just by rendering.
 */
export function useCostOfOwnership(
  org: string,
  assetUid: string | undefined,
  opts: { windowMonths?: number; enabled?: boolean } = {},
) {
  const windowMonths = opts.windowMonths ?? 24
  return useQuery({
    queryKey: costOfOwnershipKey(org, assetUid ?? '', windowMonths),
    enabled: Boolean(org && assetUid) && opts.enabled !== false,
    queryFn: () =>
      fetchJSON<CostOfOwnership>(
        `${apiBaseUrl}/api/v1/organizations/${org}/assets/${assetUid}/cost-of-ownership?window_months=${windowMonths}`,
      ),
  })
}
